
import { env } from '../../../env';
import { useCallback, useEffect, useMemo, useState } from 'react';

import APIService from '../../../services/api.service';
import { QUERY_CONFIG } from '../../../shared/utils/cache-config.utils';
import { ToastService } from '../../../services/toast.service';
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import { useQuery } from 'react-query';

export type TreeDataStructure = {
   [category: string]: {
      [subcategory: string]: string[];
   };
};

export function useTreeData() {
   const { instance } = useMsal();
   const [treeData, setTreeData] = useState<TreeDataStructure>({});
   const [showPartDetailWarning, setShowPartDetailWarning] = useState(false);
   const [showPartWarning, setShowPartWarning] = useState(false);
   const [showMaterialGradeWarning, setShowMaterialGradeWarning] = useState(false);

   const fetchAndSaveNewTokenToStorage = async () => {
      const accessTokenRequest = {
         scopes: [env.REACT_APP_AUTH_BACKEND_API_SCOPE || ''],
         account: instance.getAllAccounts()[0],
      };

      const result = await instance.acquireTokenSilent(accessTokenRequest).catch((e) => {
         instance.acquireTokenRedirect({ scopes: ['User.Read'] });
         return e;
      });

      localStorage.setItem('accessToken', result.accessToken);
   };

   const fetchTreeData = async () => {
      await fetchAndSaveNewTokenToStorage();
      return APIService.getDropDownTreeData();
   };

   const { status, data: response, error } = useQuery('treeData', fetchTreeData, QUERY_CONFIG);

   useEffect(() => {
      switch (status) {
         case 'error':
            ToastService.error(String(error));
            setTreeData({});
            break;
         case 'success':
            if (response?.data) {
               setTreeData(response.data);
            }
            break;
      }
   }, [status, response, error]);

   const reversePartLookup = useMemo(() => {
      const lookup = new Map<string, string[]>();
      Object.entries(treeData).forEach(([partDetail, parts]) => {
         Object.keys(parts).forEach((part) => {
            const existing = lookup.get(part) || [];
            lookup.set(part, _.uniq([...existing, partDetail]));
         });
      });
      return lookup;
   }, [treeData]);

   const getPartDetailOptions = useCallback((): string[] => {
      return Object.keys(treeData);
   }, [treeData]);

   const getPartOptions = useCallback(
      (partDetail?: string): string[] => {
         if (!partDetail) {
            return _.uniq(Object.values(treeData).flatMap((subcategory) => Object.keys(subcategory)));
         }
         const parts = treeData[partDetail];
         return parts ? Object.keys(parts) : [];
      },
      [treeData]
   );

   const getCompatiblePartDetails = useCallback(
      (part: string): string[] => {
         return reversePartLookup.get(part) || [];
      },
      [reversePartLookup]
   );

   const formatMaterialGradeOptions = useCallback((options: string[]): string[] => {
      if (!options || !options.length) return [];

      return _.uniq(
         options.flatMap((option) =>
            (option || '')
               .split(',')
               .map((item) => item.trim())
               .filter((item) => item.length > 0)
         )
      );
   }, []);

   const getMaterialGradeOptions = useCallback(
      (partDetail?: string, part?: string): string[] => {
         if (!part) return [];

         if (partDetail) {
            return formatMaterialGradeOptions(treeData[partDetail]?.[part] || []);
         }

         const compatiblePartDetails = getCompatiblePartDetails(part);
         const allGrades = compatiblePartDetails.flatMap((pd) => treeData[pd]?.[part] || []);
         return formatMaterialGradeOptions(allGrades);
      },
      [treeData, getCompatiblePartDetails, formatMaterialGradeOptions]
   );

   const handlePartDetailChange = useCallback(
      (value: string | string[] | null): void => {
         if (!value) {
            setShowPartDetailWarning(false);
            return;
         }
         const checkValue = Array.isArray(value) ? value[value.length - 1] : value;
         const isPredefinedValue = getPartDetailOptions().includes(checkValue);
         setShowPartDetailWarning(!isPredefinedValue);
      },
      [getPartDetailOptions]
   );

   const handlePartChange = useCallback(
      (value: string | string[] | null): void => {
         if (!value) {
            setShowPartWarning(false);
            return;
         }
         const checkValue = Array.isArray(value) ? value[value.length - 1] : value;
         const isPredefinedValue = getPartOptions().includes(checkValue);
         setShowPartWarning(!isPredefinedValue);
      },
      [getPartOptions]
   );

   const handleMaterialGradeChange = useCallback(
      (value: string | string[] | null, partDetail?: string, part?: string): void => {
         if (!value || !part) {
            setShowMaterialGradeWarning(false);
            return;
         }
         const checkValue = Array.isArray(value) ? value[value.length - 1] : value;
         const validOptions = getMaterialGradeOptions(partDetail, part);
         setShowMaterialGradeWarning(!validOptions.includes(checkValue));
      },
      [getMaterialGradeOptions]
   );

   const getFilteredParts = useCallback(
      (partDetail: string | undefined, materialParts: string[]): string[] => {
         if (!partDetail) return materialParts;
         const parts = treeData[partDetail];
         if (!parts) return [];

         return Object.keys(parts);
      },
      [treeData]
   );

   return {
      isLoading: status === 'loading',
      showPartDetailWarning,
      showPartWarning,
      showMaterialGradeWarning,
      getPartDetailOptions,
      getPartOptions,
      getMaterialGradeOptions,
      getCompatiblePartDetails,
      handlePartDetailChange,
      handlePartChange,
      handleMaterialGradeChange,
      getFilteredParts,
   };
}
